import type {
  CustomerExperienceScoreAggregate,
  TopicId,
  TopicOutcomeCorrelation,
  TopicOverview,
} from "@repo/app-types/entities/topic";

/**
 * Volume-weighted percentage-point gap between the topic's win rate and
 * the overall scope rate:
 *
 *   totalRate = (nWithTopicAndOutcome + nWithoutTopicAndOutcome)
 *               / (nWithTopic + nWithoutTopic)
 *   rawDelta  = (nWithTopicAndOutcome / nWithTopic − totalRate) × 100
 *   weighted  = rawDelta × (nWithTopic / (nWithTopic + nWithoutTopic))
 *
 * The share multiplier mirrors the customer-experience metric, so a
 * small edge across many calls can outrank a big edge on a handful.
 * Positive = the topic wins more often than the scope as a whole
 * (booster); negative = less often (detractor). Returns `null` when
 * either arm is empty — with no calls outside the topic there's no
 * meaningful baseline to compare against.
 *
 * **Parent rows do not equal the sum of their subtopic rows.** The
 * outcome counts this formula consumes are per-grading with
 * dedupe-by-OR at the parent level: a call that matches two subtopics
 * of the same parent contributes 1 to the parent's
 * `nWithTopic`/`nWithTopicAndOutcome`, but 1 to *each* subtopic's
 * counts. See the comment in `fetchTopicOutcomeCountsInClickHouse` for
 * the underlying rollup rule.
 */
export function computeTopicWinDelta(
  correlation: TopicOutcomeCorrelation,
): number | null {
  const {
    nWithTopic,
    nWithTopicAndOutcome,
    nWithoutTopic,
    nWithoutTopicAndOutcome,
  } = correlation;
  if (nWithTopic === 0 || nWithoutTopic === 0) return null;
  const totalCalls = nWithTopic + nWithoutTopic;
  const withRate = nWithTopicAndOutcome / nWithTopic;
  const totalRate =
    (nWithTopicAndOutcome + nWithoutTopicAndOutcome) / totalCalls;
  const rawDelta = (withRate - totalRate) * 100;
  const topicShare = nWithTopic / totalCalls;
  return Math.round(rawDelta * topicShare * 100) / 100;
}

/**
 * Per-row signed win-delta (percentage points) keyed by topic id.
 *
 * - Leaf rows use their own correlation.
 * - Parents inherit their strongest sub-topic — the child with the
 *   largest `|deltaPp|` — so the parent's pill summarizes the
 *   category's strongest signal. Parents whose children all lack data
 *   fall back to their own measured correlation.
 * - `null` means the row has no usable correlation data — the UI
 *   renders "—" and the sort treats it as lowest.
 */
export function buildTopicImpactLookup(
  rows: readonly TopicOverview[],
): Map<TopicId, number | null> {
  const ownDeltaByTopic = new Map<TopicId, number | null>();
  for (const row of rows) {
    ownDeltaByTopic.set(
      row.topicId,
      row.outcomeCorrelation
        ? computeTopicWinDelta(row.outcomeCorrelation)
        : null,
    );
  }

  // For each parent, the sub-topic delta with the largest absolute
  // magnitude — keyed by parent id so leaf rows miss and fall through.
  const strongestChildByParent = new Map<TopicId, number>();
  for (const row of rows) {
    if (!row.parentId) continue;
    const own = ownDeltaByTopic.get(row.topicId);
    if (own == null) continue;
    const incumbent = strongestChildByParent.get(row.parentId);
    if (incumbent === undefined || Math.abs(own) > Math.abs(incumbent)) {
      strongestChildByParent.set(row.parentId, own);
    }
  }

  const impactByTopic = new Map<TopicId, number | null>();
  for (const row of rows) {
    const inherited = strongestChildByParent.get(row.topicId);
    impactByTopic.set(
      row.topicId,
      inherited !== undefined
        ? inherited
        : (ownDeltaByTopic.get(row.topicId) ?? null),
    );
  }
  return impactByTopic;
}

/**
 * Map a raw 1-5 customer-experience score onto a 0-100 scale —
 * `1 → 0, 2 → 25, 3 → 50, 4 → 75, 5 → 100`.
 */
export function customerExperienceScoreToPercent(score: number): number {
  return (score - 1) * 25;
}

export type TopicCustomerExperienceEntry =
  | { kind: "leaf"; weightedScore: number }
  | {
      kind: "parent";
      bestWeightedScore: number;
      worstWeightedScore: number;
    };

/**
 * Turn a topic's raw `CustomerExperienceScoreAggregate` into its displayed
 * weighted score, measured against the scope-wide baseline totals.
 *
 * Both averages are mapped onto the 0-100 scale before differencing;
 * the resulting per-call gap is then scaled by the topic's share of
 * scored calls, so a big swing on a rare topic stays small. Returns
 * `null` when the topic has no scored calls — or when the scope has
 * none — since there's no gap to render against nothing.
 */
function computeCustomerExperienceScore(
  topic: CustomerExperienceScoreAggregate,
  baseline: CustomerExperienceScoreAggregate,
): number | null {
  if (topic.callCount === 0 || baseline.callCount === 0) {
    return null;
  }
  const topicAverage = topic.sum / topic.callCount;
  const baselineAverage = baseline.sum / baseline.callCount;
  const delta =
    customerExperienceScoreToPercent(topicAverage) -
    customerExperienceScoreToPercent(baselineAverage);
  const proportion = topic.callCount / baseline.callCount;
  return Math.round(delta * proportion * 100) / 100;
}

/**
 * Per-row customer-experience entry keyed by topic id.
 *
 * - Leaves resolve to their own weighted score (or `null` when they
 *   have no scored calls).
 * - Parents resolve to the weighted-score bounds across their leaf
 *   children — the table reads `bestWeightedScore` when sorting
 *   descending and `worstWeightedScore` when ascending, so a parent
 *   sits next to its most extreme subtopic in either direction. A
 *   parent with no scored children resolves to `null`.
 */
export function buildTopicCustomerExperienceLookup(
  overviewRows: readonly TopicOverview[],
  baseline: CustomerExperienceScoreAggregate,
): ReadonlyMap<TopicId, TopicCustomerExperienceEntry | null> {
  const result = new Map<TopicId, TopicCustomerExperienceEntry | null>();

  const isParent = (row: TopicOverview) => row.leafIds.length > 0;

  // First pass: every leaf's own score. Parents read their children's
  // values out of this map in the second pass.
  const leafScoreByTopic = new Map<TopicId, number | null>();
  for (const row of overviewRows) {
    if (isParent(row)) {
      continue;
    }
    leafScoreByTopic.set(
      row.topicId,
      computeCustomerExperienceScore(row.customerExperience, baseline),
    );
  }

  for (const row of overviewRows) {
    if (!isParent(row)) {
      const score = leafScoreByTopic.get(row.topicId);
      result.set(
        row.topicId,
        score == null ? null : { kind: "leaf", weightedScore: score },
      );
      continue;
    }
    // Parent: weighted-score bounds across leaf children with data.
    // Children with no scored calls are skipped; a parent with no
    // scored children stays null.
    let best: number | null = null;
    let worst: number | null = null;
    for (const leafId of row.leafIds) {
      const childScore = leafScoreByTopic.get(leafId);
      if (childScore == null) {
        continue;
      }
      best = Math.max(best ?? childScore, childScore);
      worst = Math.min(worst ?? childScore, childScore);
    }
    result.set(
      row.topicId,
      best === null || worst === null
        ? null
        : {
            kind: "parent",
            bestWeightedScore: best,
            worstWeightedScore: worst,
          },
    );
  }
  return result;
}
